import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from "react-router-dom";
import { GlobalStateProvider } from "./helpers/GlobalStateProvider";
import { ModalProvider } from "./helpers/ModalProvider";

// Your other components:
import Dashboard from "./views/Dashboard";
import Signin from "./views/Signin";
import Signup from "./views/Signup";
import Settings from "./views/settings/Settings";
import ChatContainer from "./views/chatbot/ChatContainer";
import { Toaster } from "./components/ui/toaster";
import { CannotConnectWalletModal } from "./views/modals/CannotConnectWalletModal";
import DeleteAPIKeyModal from "./views/modals/DeleteApiKeyModal";
import EditAPIKeysModal from "./views/modals/EditApiKeyModal";
import { WhatIsStakingModal } from "./views/modals/WhatIsStakingModal";
import StakeModals from "./views/modals/StakeModals";
import DeleteAuthModal from "./views/modals/DeleteAuthModal";
import AddEmailModal from "./views/modals/AddEmailModal";
import UpdateAuthMethodModal from "./views/modals/UpdateAuthModal";
import RenameChatModal from "./views/modals/RenameChatModal";
import DeleteChatModal from "./views/modals/DeleteChatModal";
import { Provider } from "./components/ui/provider";
import DeleteAccountModal from "./views/modals/DeleteAccountModal";
import ResetPasswordModal from "./views/modals/ResetPasswordModal";
import SelectNetworkDialog from "./views/modals/SelectNetworkDialog";
import ClaimBroadcastModal from "./views/modals/ClaimBroadcastModal";
import ApiDocs from "./views/ApiDocs";
import SignupConfirm from "./views/SignupConfirm";
import SignupConfirmed from "./views/SignupConfirmed";
import ResetPasswordSuccess from "./views/PasswordResetSuccess";
import ResetPasswordPage from "./views/ResetPassword";
import ExpiredLink from "./views/ExpiredLink";
import UpdatePasswordModal from "./views/modals/UpdateAuthPasswordModal";
import VerifyEmailModal from "./views/modals/VerifyEmailModal";
import Maintenance from "./views/Maintenance";
import TosAgreementWrapper from "./views/TosAgreementWrapper";
import CreatePrepaidKeyModal from "./views/modals/CreatePrepaidKeyModal";
import PrepaidKeyCreatedModal from "./views/modals/PrepaidKeyCreatedModal";
import RefillPrepaidKeyModal from "./views/modals/RefillPrepaidKeyModal";
import PrepaidKeyRefilledModal from "./views/modals/PrepaidKeyRefilledModal";

const RedirectToExternal: React.FC<{ url: string }> = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);

  return null; // Render nothing as the page is being redirected
};

// Keeps users on /maintenance when forced via localStorage flag
const MaintenanceGuard: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const forced = false//true
    const bypass = false//localStorage.getItem('bypass') === 'true';
    if (forced && !bypass && location.pathname !== '/maintenance') {
      window.location.replace('/maintenance');
    }
  }, [location]);
  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Backend selection and maintenance bypass only available in development mode
    if (process.env.NODE_ENV === 'development') {
      const newBackendParam = params.get('newbackend');
      if (newBackendParam === 'true') {
        localStorage.setItem('use-new-backend', 'true');
      } else if (newBackendParam === 'false') {
        localStorage.setItem('use-new-backend', 'false');
      }
    }
    if (process.env.NODE_ENV === 'development') {
      const bypassMaintenanceParam = params.get('bypass');
      if (bypassMaintenanceParam === 'true') {
        localStorage.setItem('bypass', 'true');
      } else if (bypassMaintenanceParam === 'false') {
        localStorage.setItem('bypass', 'false');
      }
    }
  }, []);

  return (
    <Provider>
      <Router>
        <ModalProvider>
          <GlobalStateProvider>
            <MaintenanceGuard />
            <TosAgreementWrapper>
              <Routes>
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/signin" element={<Signin />} />
                <Route path="/confirm_email" element={<SignupConfirm />} />
                <Route path="/email_confirmed" element={<SignupConfirmed />} />
                <Route path="/reset_password_success" element={<ResetPasswordSuccess />} />
                <Route path="/reset_password" element={<ResetPasswordPage />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/chatbot" element={<ChatContainer />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/expired_link" element={<ExpiredLink />} />
                <Route path="*" element={<RedirectToExternal url="https://splash.coincap.io" />} />
              </Routes>
            </TosAgreementWrapper>

            {/* Modals */}
            <DeleteAPIKeyModal />
            <EditAPIKeysModal />
            <WhatIsStakingModal />
            <StakeModals />
            <DeleteAuthModal />
            <AddEmailModal />
            <UpdateAuthMethodModal />
            <UpdatePasswordModal />
            <RenameChatModal />
            <DeleteChatModal />
            <CannotConnectWalletModal />
            <DeleteAccountModal />
            <ResetPasswordModal />
            <SelectNetworkDialog />
            <ClaimBroadcastModal />
            <VerifyEmailModal />
            <CreatePrepaidKeyModal />
            <PrepaidKeyCreatedModal />
            <RefillPrepaidKeyModal />
            <PrepaidKeyRefilledModal />

            {/* Toast. */}
            <Toaster />
          </GlobalStateProvider>
        </ModalProvider>
      </Router>
    </Provider>
  );
};

export default App;
